export type CookieApp = 'auth' | 'camPortal' | 'mar' | 'help' | 'legal' | 'admin';

export type CookieType = 'functional' | 'analytics' | 'targeting';

export interface CookieInfo {
	name: string;
	duration: string;
	description: string;
}

export interface CookieTypeConfig {
	type?: CookieType;
	title?: string;
	description?: string;
	required?: boolean;
	cookies?: CookieInfo[];
}

export type ScriptType = 'google-tag-manager';

export interface ScriptConfig {
	type: ScriptType;
	id: string;
}

export interface AppCookieConfig {
	types: Partial<Record<CookieType, CookieTypeConfig>>;
	scripts?: Partial<Record<CookieType, ScriptConfig[]>>;
}

export const COOKIE_TYPE_DEFAULTS: Record<
	CookieType,
	Pick<CookieTypeConfig, 'type' | 'title' | 'description' | 'required'>
> = {
	functional: {
		type: 'functional',
		title: 'Functional Cookies',
		description: `
						These cookies are required for the basic operation of the website.
						They are set automatically when you access our services and cannot be disabled through the cookie banner.
						These cookies allow the website to remember your choices (such as language preferences) and
						provide a more personalized experience.
					`,
		required: true,
	},
	analytics: {
		type: 'analytics',
		title: 'Analytics Cookies',
		description: `
						We use these cookies to collect information about how visitors use our website.
						This helps us understand which parts of the site need improvement and how appealing it is to users.
					`,
		required: false,
	},
	targeting: {
		type: 'targeting',
		title: 'Targeting Cookies',
		description: `
						These cookies are used to track your activity on and off our website.
						They can be used to display targeted ads that are more relevant to you based on your interests.
					`,
		required: false,
	},
};

export const APP_COOKIE_CONFIG: Record<CookieApp, AppCookieConfig> = {
	auth: {
		types: {
			functional: {
				cookies: [
					{
						name: 'cf_clearance',
						duration: 'Session',
						description: 'Used by Cloudflare Turnstile to verify that you are not a robot.',
					},
					{
						name: 'TDC_itoken',
						duration: 'Session',
						description: 'Used by Tencent Captcha to determine whether verification requests come from the same device. Used only for visitors from China.',
					},
					{
						name: '_TDID_CK',
						duration: '1 month',
						description: 'Used by Tencent Captcha to support device identification and bot protection. Used only for visitors from China.',
					},
				],
			},
		},
		scripts: {},
	},
	camPortal: {
		types: {
			functional: {
				cookies: [
					{
						name: 'locale',
						duration: '1 year',
						description: 'Stores your chosen language for a consistent browsing experience.',
					},
					{
						name: 'cf_clearance',
						duration: 'Session',
						description: 'Used by Cloudflare Turnstile to verify that you are not a robot.',
					},
					{
						name: 'TDC_itoken',
						duration: 'Session',
						description: 'Used by Tencent Captcha to determine whether verification requests come from the same device. Used only for visitors from China.',
					},
					{
						name: '_TDID_CK',
						duration: '1 month',
						description: 'Used by Tencent Captcha to support device identification and bot protection. Used only for visitors from China.',
					},
				],
			},
		},
		scripts: {},
	},
	mar: {
		types: {
			functional: {
				cookies: [
					{
						name: 'activeAccountCode',
						duration: 'Session',
						description: 'Helps us remember which account you are currently using.',
					},
					{
						name: 'Currency',
						duration: '2 years',
						description: 'Saves your preferred currency, so prices always show the way you expect.',
					},
					{
						name: 'locale',
						duration: '1 year',
						description: 'Stores your chosen language for a consistent browsing experience.',
					},
					{
						name: 'jwt-token',
						duration: 'Session',
						description: 'Lets you stay securely signed in while you browse.',
					},
					{
						name: 'MeasurementSystem',
						duration: '2 years',
						description: 'Keeps your preferred measurement system, such as metric or imperial.',
					},
					{
						name: 'showListingPrices',
						duration: '1 year',
						description: 'Remembers whether you prefer to see prices in gallery listings.',
					},
					{
						name: '__stripe_mid',
						duration: '1 year',
						description: 'Used by Stripe to provide secure payment processing.',
					},
					{
						name: '__stripe_sid',
						duration: '1 hour',
						description: 'Used by Stripe to manage your active checkout session securely.',
					},
					{
						name: 'cf_clearance',
						duration: 'Session',
						description: 'Used by Cloudflare Turnstile to verify that you are not a robot.',
					},
					{
						name: 'TDC_itoken',
						duration: 'Session',
						description: 'Used by Tencent Captcha to determine whether verification requests come from the same device. Used only for visitors from China.',
					},
					{
						name: '_TDID_CK',
						duration: '1 month',
						description: 'Used by Tencent Captcha to support device identification and bot protection. Used only for visitors from China.',
					},
				],
			},
			analytics: {
				cookies: [
					{
						name: '_ga',
						duration: '2 years',
						description: 'Helps us understand how visitors use the website, so we can improve your experience.',
					},
					{
						name: '_ga_NW6MWG27GY',
						duration: '2 years',
						description: 'Tracks site usage in a more detailed way to help us analyze performance and user behavior.',
					},
				],
			},
		},
		scripts: {
			analytics: [
				{
					type: 'google-tag-manager',
					id: 'GTM-MV36F4LN',
				},
			],
		},
	},
	help: {
		types: {
			functional: {
				cookies: [
					{
						name: '',
						duration: '',
						description: '',
					},
				],
			},
			analytics: {
				cookies: [
					{
						name: '',
						duration: '',
						description: '',
					},
				],
			},
		},
		scripts: {},
	},
	legal: {
		types: {
			functional: {
				cookies: [
					{
						name: '',
						duration: '',
						description: '',
					},
				],
			},
		},
		scripts: {},
	},
	admin: {
		types: {},
	},
};
